/**
 * CACFP Meal Count & Attendance Page
 * Child and Adult Care Food Program tracking
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import { format, startOfWeek, endOfWeek, addDays, addWeeks, subWeeks, parseISO, startOfMonth, endOfMonth, startOfYear, endOfYear, eachWeekOfInterval } from "date-fns";
import { 
  ArrowLeft, RefreshCw, Printer, ChevronLeft, ChevronRight, 
  Utensils, Coffee, Cookie, Moon, Sun, Check, X, FileSpreadsheet,
  FileText, Calendar, CalendarRange, MapPin, Pencil, PenLine, Users, Plus, Trash2, AlertTriangle
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import PageHero, { HERO_IMAGES } from "@/components/common/PageHero";
import FeatureGate from "@/components/FeatureGate";
import PrintReportHeader from "@/components/common/PrintReportHeader";
import AppShell from "@/components/layout/AppShell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MEAL_TYPES = [
  { key: "breakfast", label: "BK", fullLabel: "Breakfast", icon: Coffee, color: "bg-amber-100 text-amber-700" },
  { key: "amSnack", label: "AM", fullLabel: "AM Snack", icon: Cookie, color: "bg-orange-100 text-orange-700" },
  { key: "lunch", label: "L", fullLabel: "Lunch", icon: Utensils, color: "bg-green-100 text-green-700" },
  { key: "pmSnack", label: "PM", fullLabel: "PM Snack", icon: Cookie, color: "bg-blue-100 text-blue-700" },
  { key: "supper", label: "S", fullLabel: "Supper", icon: Moon, color: "bg-purple-100 text-purple-700" }
];

const ATTENDANCE_OPTIONS = [
  { value: "", label: "-" },
  { value: "P", label: "P", fullLabel: "Present" },
  { value: "A", label: "A", fullLabel: "Absent" },
  { value: "N", label: "N", fullLabel: "Not Scheduled" }
];

const CACFPPage = () => {
  const navigate = useNavigate();
  const { organization, user } = useAuth();
  const [children, setChildren] = useState([]);
  const [records, setRecords] = useState({});
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Available symbols for staff assignment - Extended list for larger staff
  const STAFF_SYMBOLS = [
    '★', '●', '▲', '■', '◆', '○', '□', '△', '♦', '♠', '♥', '♣',
    '✦', '✧', '◉', '◎', '▼', '▽', '◇', '⬟', '⬡', '⬢', '✶', '✴',
    '⊕', '⊗', '⊛', '⊙', '⊚', '⬤', '◐', '◑', '◒', '◓', '⬣', '⬠'
  ];
  
  // System users (from Manage Staff)
  const [systemUsers, setSystemUsers] = useState([]);
  
  // Meal service address for CACFP compliance
  const [mealServiceAddress, setMealServiceAddress] = useState(() => {
    return localStorage.getItem('cacfp_meal_service_address') || '';
  });
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [tempAddress, setTempAddress] = useState('');
  
  // Staff Roster with Symbols for CACFP
  const [staffRoster, setStaffRoster] = useState(() => {
    const saved = localStorage.getItem('cacfp_staff_roster');
    return saved ? JSON.parse(saved) : [];
  });
  const [showStaffRosterDialog, setShowStaffRosterDialog] = useState(false);
  const [newStaffName, setNewStaffName] = useState('');
  const [newStaffSymbol, setNewStaffSymbol] = useState(STAFF_SYMBOLS[0]); // Default to first symbol
  const [selectedUserId, setSelectedUserId] = useState(''); // Selected user from system
  const [activeStaff, setActiveStaff] = useState(() => {
    const saved = localStorage.getItem('cacfp_active_staff');
    return saved ? JSON.parse(saved) : null;
  });
  
  // Fetch system users for staff selection
  const fetchSystemUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      // Filter out special staff accounts
      const filteredUsers = response.data.filter(u => !u.isSpecialStaff);
      setSystemUsers(filteredUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };
  
  // Auto-select current user as active staff on load (if they're in the roster)
  useEffect(() => {
    if (user && staffRoster.length > 0 && !activeStaff) {
      const matchingStaff = staffRoster.find(s => s.userId === user.id);
      if (matchingStaff) {
        setCurrentStaff(matchingStaff);
      }
    }
  }, [user, staffRoster]);
  
  // Fetch users when dialog opens
  useEffect(() => {
    if (showStaffRosterDialog) {
      fetchSystemUsers();
    }
  }, [showStaffRosterDialog]);
  
  // Save staff roster to localStorage
  const saveStaffRoster = (roster) => {
    localStorage.setItem('cacfp_staff_roster', JSON.stringify(roster));
    setStaffRoster(roster);
  };
  
  // Add new staff member (from system users dropdown)
  const addStaffMember = () => {
    if (!selectedUserId || !newStaffSymbol) return;
    
    const selectedUser = systemUsers.find(u => u.id === selectedUserId);
    if (!selectedUser) return;
    
    // Check if user is already in roster
    if (staffRoster.some(s => s.userId === selectedUserId)) {
      toast.error("This staff member is already in the roster");
      return;
    }
    
    const newRoster = [...staffRoster, { 
      id: Date.now().toString(),
      userId: selectedUser.id,
      name: selectedUser.name || selectedUser.email, 
      email: selectedUser.email,
      symbol: newStaffSymbol,
      signature: ''
    }];
    saveStaffRoster(newRoster);
    setSelectedUserId('');
    // Set to next available symbol
    const usedSymbols = newRoster.map(s => s.symbol);
    const nextSymbol = STAFF_SYMBOLS.find(s => !usedSymbols.includes(s)) || '';
    setNewStaffSymbol(nextSymbol);
    
    // Auto-select if this is the current user
    if (selectedUser.id === user?.id) {
      setCurrentStaff(newRoster[newRoster.length - 1]);
    }
    
    toast.success(`Added ${selectedUser.name || selectedUser.email} to staff roster`);
  };
  
  // Get users not already in roster
  const getAvailableUsers = () => {
    const rosterUserIds = staffRoster.map(s => s.userId);
    return systemUsers.filter(u => !rosterUserIds.includes(u.id));
  };
  
  // Remove staff member
  const removeStaffMember = (id) => {
    const staffToRemove = staffRoster.find(s => s.id === id);
    const newRoster = staffRoster.filter(s => s.id !== id);
    saveStaffRoster(newRoster);
    
    // Clear active staff if removed
    if (activeStaff?.id === id) {
      setActiveStaff(null);
      localStorage.removeItem('cacfp_active_staff');
    }
    
    if (staffToRemove) {
      toast.success(`Removed ${staffToRemove.name} from staff roster`);
    }
  };
  
  // Update staff signature
  const updateStaffSignature = (id, signature) => {
    const newRoster = staffRoster.map(s => 
      s.id === id ? { ...s, signature } : s
    );
    saveStaffRoster(newRoster);
  };
  
  // Set active staff (who is currently entering data)
  const setCurrentStaff = (staff) => {
    setActiveStaff(staff);
    localStorage.setItem('cacfp_active_staff', JSON.stringify(staff));
  };
  
  // Get available symbols (not already assigned)
  const getAvailableSymbols = () => {
    const usedSymbols = staffRoster.map(s => s.symbol);
    return STAFF_SYMBOLS.filter(s => !usedSymbols.includes(s));
  };
  // Print Staff Roster with symbols and information
  const printStaffRoster = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    
    // Header
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("CACFP Staff Roster", pageWidth / 2, 20, { align: "center" });
    
    // Organization name if available
    if (organization?.name) {
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(organization.name, pageWidth / 2, 28, { align: "center" });
    }
    
    // Date
    doc.setFontSize(10);
    doc.text(`Generated: ${format(new Date(), "MMMM d, yyyy")}`, pageWidth / 2, 35, { align: "center" });
    
    // Table data
    const tableData = staffRoster.map(staff => [
      staff.symbol,
      staff.name,
      staff.email || '-',
      staff.signature || '-'
    ]);
    
    // Add table
    autoTable(doc, {
      startY: 45,
      head: [['Symbol', 'Staff Name', 'Email', 'Signature']],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: [26, 54, 93],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center'
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 20, fontSize: 14 },
        1: { halign: 'left', cellWidth: 50 },
        2: { halign: 'left', cellWidth: 60 },
        3: { halign: 'center', cellWidth: 40, fontStyle: 'italic' }
      },
      styles: {
        fontSize: 10,
        cellPadding: 4
      }
    });
    
    // Symbol Legend at bottom
    const finalY = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("Symbol Legend:", 14, finalY);
    doc.setFont("helvetica", "normal");
    
    let legendY = finalY + 8;
    staffRoster.forEach((staff, idx) => {
      doc.setFontSize(12);
      doc.text(staff.symbol, 20, legendY);
      doc.setFontSize(10);
      doc.text(`= ${staff.name}`, 30, legendY);
      legendY += 6;
    });
    
    // Footer with instructions
    doc.setFontSize(8);
    doc.setTextColor(100);
    doc.text("This roster identifies staff members by their assigned symbols for CACFP meal count documentation.", 14, doc.internal.pageSize.height - 15);
    doc.text("Staff symbols appear on meal count sheets to track who recorded each entry.", 14, doc.internal.pageSize.height - 10);
    
    // Open in new window for printing
    const pdfBlob = doc.output('blob');
    const url = URL.createObjectURL(pdfBlob);
    window.open(url, '_blank');
  };
  
  // Signature state for CACFP compliance (Director)
  const [showSignatureDialog, setShowSignatureDialog] = useState(false);
  const [staffSignature, setStaffSignature] = useState(() => {
    return localStorage.getItem('cacfp_staff_signature') || '';
  });
  const [directorName, setDirectorName] = useState(() => {
    return localStorage.getItem('cacfp_director_name') || '';
  });
  const [directorSignature, setDirectorSignature] = useState(() => {
    return localStorage.getItem('cacfp_director_signature') || '';
  });
  const [tempStaffSig, setTempStaffSig] = useState('');
  const [tempDirName, setTempDirName] = useState('');
  const [tempDirSig, setTempDirSig] = useState('');
  const [includeDigitalSignatures, setIncludeDigitalSignatures] = useState(true);
  
  // Save signatures to localStorage
  const saveSignatures = () => {
    localStorage.setItem('cacfp_staff_signature', tempStaffSig);
    localStorage.setItem('cacfp_director_name', tempDirName);
    localStorage.setItem('cacfp_director_signature', tempDirSig);
    setStaffSignature(tempStaffSig);
    setDirectorName(tempDirName);
    setDirectorSignature(tempDirSig);
    setShowSignatureDialog(false);
  };
  
  // Save address to localStorage when it changes
  const saveMealServiceAddress = () => {
    localStorage.setItem('cacfp_meal_service_address', tempAddress);
    setMealServiceAddress(tempAddress);
    setShowAddressDialog(false);
  };
  
  // Report generation state
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportType, setReportType] = useState("month"); // "month", "year", "monthSummary", "yearSummary"
  const [reportMonth, setReportMonth] = useState(new Date().getMonth());
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [generatingReport, setGeneratingReport] = useState(false);
  
  // Week selection
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    return startOfWeek(today, { weekStartsOn: 1 }); // Monday
  });
  
  // Get week days (Mon-Sun, 7 days for centers that operate on weekends)
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));
  const weekEnd = addDays(currentWeekStart, 6); // Sunday
  
  useEffect(() => {
    fetchChildren();
  }, []);
  
  useEffect(() => {
    if (children.length > 0) {
      fetchRecords();
    }
  }, [currentWeekStart, children]);
  
  const fetchChildren = async () => {
    try {
      const res = await axios.get(`${API}/cacfp/sync-children`);
      setChildren(res.data?.children || []);
    } catch (error) {
      console.error("Error fetching children:", error);
      toast.error("Failed to load children");
    }
  };
  
  const syncChildren = async () => {
    setSyncing(true);
    await fetchChildren();
    toast.success(`Synced ${children.length} children`);
    setSyncing(false);
  };
  
  // Category configuration
  const CATEGORIES = [
    { key: "full_time", label: "Full Time", color: "bg-blue-500" },
    { key: "regular", label: "Full Time", color: "bg-blue-500" },
    { key: "part_time", label: "Part Time", color: "bg-green-500" },
    { key: "school_age", label: "School Age", color: "bg-purple-500" },
    { key: "after_school", label: "After School", color: "bg-orange-500" }
  ];
  
  // Group and sort children by category (alphabetically within each group)
  const getGroupedChildren = () => {
    const groups = {
      full_time: [],
      part_time: [],
      school_age: [],
      after_school: [],
      other: []
    };
    
    children.forEach(child => {
      const category = child.category || "other";
      if (category === "full_time" || category === "regular") {
        groups.full_time.push(child);
      } else if (category === "part_time") {
        groups.part_time.push(child);
      } else if (category === "school_age") {
        groups.school_age.push(child);
      } else if (category === "after_school") {
        groups.after_school.push(child);
      } else {
        groups.other.push(child);
      }
    });
    
    // Sort each group alphabetically by name
    Object.keys(groups).forEach(key => {
      groups[key].sort((a, b) => a.name.localeCompare(b.name));
    });
    
    return groups;
  };
  
  const groupedChildren = getGroupedChildren();
  
  const getCategoryLabel = (key) => {
    const labels = {
      full_time: "Full Time",
      part_time: "Part Time",
      school_age: "School Age",
      after_school: "After School",
      other: "Other"
    };
    return labels[key] || key;
  };
  
  const getCategoryColor = (key) => {
    const colors = {
      full_time: "bg-blue-600",
      part_time: "bg-green-600",
      school_age: "bg-purple-600",
      after_school: "bg-orange-600",
      other: "bg-gray-600"
    };
    return colors[key] || "bg-gray-600";
  };
  
  const fetchRecords = async () => {
    setLoading(true);
    try {
      const startDate = format(currentWeekStart, "yyyy-MM-dd");
      const endDate = format(addDays(currentWeekStart, 6), "yyyy-MM-dd"); // Include Sat & Sun
      const res = await axios.get(`${API}/cacfp/records?start_date=${startDate}&end_date=${endDate}`);
      
      // Convert array to map for easy lookup
      const recordsMap = {};
      (res.data?.records || []).forEach(r => {
        const key = `${r.childId}_${r.date}`;
        recordsMap[key] = r;
      });
      setRecords(recordsMap);
    } catch (error) {
      console.error("Error fetching records:", error);
    }
    setLoading(false);
  };
  
  const getRecord = (childId, date) => {
    const key = `${childId}_${format(date, "yyyy-MM-dd")}`;
    return records[key] || {};
  };
  
  const toggleMeal = async (childId, date, mealType) => {
    const dateStr = format(date, "yyyy-MM-dd");
    const key = `${childId}_${dateStr}`;
    const currentRecord = records[key] || {};
    
    // Check if child is present - only allow meal marking if attendance is "P"
    if (currentRecord.attendance !== "P") {
      toast.error("Attendance Required: Mark child as Present (P) before recording meals", {
        description: "Set attendance in the rightmost column first"
      });
      return;
    }
    
    const newValue = !currentRecord[mealType];
    
    // Track who entered this data - use activeStaff symbol or 'X' as default
    const currentEnteredBy = currentRecord.enteredBy || {};
    const updatedEnteredBy = { ...currentEnteredBy };
    if (newValue) {
      updatedEnteredBy[mealType] = activeStaff?.symbol || 'X';
    } else {
      delete updatedEnteredBy[mealType];
    }
    
    // Optimistic update
    setRecords(prev => ({
      ...prev,
      [key]: { ...currentRecord, [mealType]: newValue, enteredBy: updatedEnteredBy, childId, date: dateStr }
    }));
    
    try {
      await axios.put(`${API}/cacfp/records/${childId}/${dateStr}`, {
        [mealType]: newValue,
        enteredBy: newValue ? { [mealType]: activeStaff?.symbol || 'X' } : {}
      });
    } catch (error) {
      console.error("Error updating meal:", error);
      toast.error("Failed to update");
      // Revert on error
      setRecords(prev => ({
        ...prev,
        [key]: currentRecord
      }));
    }
  };
  
  const updateAttendance = async (childId, date, attendance) => {
    const dateStr = format(date, "yyyy-MM-dd");
    const key = `${childId}_${dateStr}`;
    const currentRecord = records[key] || {};
    
    // If attendance is not Present, clear all meals
    const shouldClearMeals = attendance !== "P";
    
    // Track who entered this data
    const currentEnteredBy = currentRecord.enteredBy || {};
    const updatedEnteredBy = { ...currentEnteredBy };
    if (activeStaff) {
      updatedEnteredBy.attendance = activeStaff.symbol;
    }
    if (shouldClearMeals) {
      // Clear meal entry tracking when clearing meals
      delete updatedEnteredBy.breakfast;
      delete updatedEnteredBy.amSnack;
      delete updatedEnteredBy.lunch;
      delete updatedEnteredBy.pmSnack;
      delete updatedEnteredBy.supper;
    }
    
    const updatedRecord = {
      ...currentRecord,
      attendance,
      enteredBy: updatedEnteredBy,
      childId,
      date: dateStr
    };
    
    if (shouldClearMeals) {
      updatedRecord.breakfast = false;
      updatedRecord.amSnack = false;
      updatedRecord.lunch = false;
      updatedRecord.pmSnack = false;
      updatedRecord.supper = false;
    }
    
    // Optimistic update
    setRecords(prev => ({
      ...prev,
      [key]: updatedRecord
    }));
    
    try {
      const updateData = { 
        attendance,
        enteredBy: activeStaff ? { attendance: activeStaff.symbol } : {}
      };
      if (shouldClearMeals) {
        updateData.breakfast = false;
        updateData.amSnack = false;
        updateData.lunch = false;
        updateData.pmSnack = false;
        updateData.supper = false;
      }
      await axios.put(`${API}/cacfp/records/${childId}/${dateStr}`, updateData);
      
      if (shouldClearMeals && (currentRecord.breakfast || currentRecord.amSnack || currentRecord.lunch || currentRecord.pmSnack || currentRecord.supper)) {
        toast.info("Meals cleared - child not marked Present");
      }
    } catch (error) {
      console.error("Error updating attendance:", error);
      toast.error("Failed to update attendance");
    }
  };
  
  // Calculate totals
  const getDailyTotals = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    const totals = { breakfast: 0, amSnack: 0, lunch: 0, pmSnack: 0, supper: 0, present: 0, absent: 0 };
    
    children.forEach(child => {
      const record = getRecord(child.id, date);
      if (record.breakfast) totals.breakfast++;
      if (record.amSnack) totals.amSnack++;
      if (record.lunch) totals.lunch++;
      if (record.pmSnack) totals.pmSnack++;
      if (record.supper) totals.supper++;
      if (record.attendance === "P") totals.present++;
      if (record.attendance === "A") totals.absent++;
    });
    
    return totals;
  };
  
  const getWeeklyTotals = () => {
    const totals = { breakfast: 0, amSnack: 0, lunch: 0, pmSnack: 0, supper: 0, present: 0, absent: 0 };
    
    weekDays.forEach(day => {
      const daily = getDailyTotals(day);
      totals.breakfast += daily.breakfast;
      totals.amSnack += daily.amSnack;
      totals.lunch += daily.lunch;
      totals.pmSnack += daily.pmSnack;
      totals.supper += daily.supper;
      totals.present += daily.present;
      totals.absent += daily.absent;
    });
    
    return totals;
  };
  
  const navigateWeek = (direction) => {
    if (direction === "prev") {
      setCurrentWeekStart(prev => subWeeks(prev, 1));
    } else {
      setCurrentWeekStart(prev => addWeeks(prev, 1));
    }
  };
  
  // Export to PDF
  const exportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const orgName = organization?.name || "Childcare Center";
    const weeklyTotals = getWeeklyTotals();
    
    // Header
    doc.setFontSize(16);
    doc.setTextColor(0, 100, 0);
    doc.text(orgName, 14, 15);
    
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text("MEAL COUNT AND ATTENDANCE", 14, 23);
    
    doc.setFontSize(10);
    doc.text(`Week of ${format(currentWeekStart, "MMMM d, yyyy")}`, 14, 30);
    
    // Meal Service Address
    if (mealServiceAddress) {
      doc.setFontSize(9);
      doc.setTextColor(80);
      doc.text(`Meals Served At: ${mealServiceAddress}`, 14, 36);
      doc.setTextColor(0, 0, 0);
    }
    
    // Legend
    doc.setFontSize(8);
    doc.text("ATT: A=Absent, P=Present, N=Not Scheduled", 200, 15);
    
    // Build table data
    const headers = [["#", "PARTICIPANT NAME"]];
    weekDays.forEach(day => {
      headers[0].push(format(day, "EEE M/d"));
      headers[0].push("BK", "AM", "L", "PM", "S", "ATT");
    });
    
    const tableData = children.map((child, idx) => {
      const row = [idx + 1, child.name];
      weekDays.forEach(day => {
        const record = getRecord(child.id, day);
        row.push(""); // Day header spacer
        // Include staff symbols in the cell content
        row.push(record.breakfast ? `X${record.enteredBy?.breakfast || ''}` : "");
        row.push(record.amSnack ? `X${record.enteredBy?.amSnack || ''}` : "");
        row.push(record.lunch ? `X${record.enteredBy?.lunch || ''}` : "");
        row.push(record.pmSnack ? `X${record.enteredBy?.pmSnack || ''}` : "");
        row.push(record.supper ? `X${record.enteredBy?.supper || ''}` : "");
        row.push(record.attendance || "");
      });
      return row;
    });
    
    // Add totals row
    const totalsRow = ["", "DAILY TOTALS"];
    weekDays.forEach(day => {
      const dt = getDailyTotals(day);
      totalsRow.push("");
      totalsRow.push(dt.breakfast || "");
      totalsRow.push(dt.amSnack || "");
      totalsRow.push(dt.lunch || "");
      totalsRow.push(dt.pmSnack || "");
      totalsRow.push(dt.supper || "");
      totalsRow.push(`P:${dt.present} A:${dt.absent}`);
    });
    tableData.push(totalsRow);
    
    autoTable(doc, {
      startY: 35,
      head: headers,
      body: tableData,
      headStyles: {
        fillColor: [34, 139, 34],
        textColor: [255, 255, 255],
        fontSize: 6,
        fontStyle: "bold"
      },
      styles: {
        fontSize: 6,
        cellPadding: 1
      },
      columnStyles: {
        0: { cellWidth: 8 },
        1: { cellWidth: 35 }
      }
    });
    
    // Weekly totals summary
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(10);
    doc.text("WEEKLY TOTALS:", 14, finalY);
    doc.setFontSize(8);
    doc.text(`Breakfast: ${weeklyTotals.breakfast}  |  AM Snack: ${weeklyTotals.amSnack}  |  Lunch: ${weeklyTotals.lunch}  |  PM Snack: ${weeklyTotals.pmSnack}  |  Supper: ${weeklyTotals.supper}`, 14, finalY + 6);
    doc.text(`Present: ${weeklyTotals.present}  |  Absent: ${weeklyTotals.absent}`, 14, finalY + 12);
    
    // Staff Reference Legend
    let sigStartY = finalY + 20;
    const pageWidth = doc.internal.pageSize.getWidth();
    
    if (staffRoster.length > 0) {
      doc.setFillColor(250, 250, 255);
      doc.roundedRect(14, sigStartY, pageWidth - 28, 18, 2, 2, 'F');
      doc.setDrawColor(100, 100, 200);
      doc.roundedRect(14, sigStartY, pageWidth - 28, 18, 2, 2, 'S');
      
      doc.setFontSize(8);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(50, 50, 150);
      doc.text("STAFF REFERENCE:", 20, sigStartY + 6);
      
      doc.setFont("helvetica", "normal");
      doc.setTextColor(0, 0, 0);
      const legendText = staffRoster.map(s => `${s.symbol} = ${s.name}`).join('   |   ');
      doc.text(legendText, 20, sigStartY + 13);
      
      sigStartY += 22;
    }
    
    // Multi-Staff Signature Section
    doc.setDrawColor(100);
    doc.setLineWidth(0.3);
    
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text("STAFF SIGNATURES (Prepared By):", 14, sigStartY);
    doc.setFont("helvetica", "normal");
    
    let sigY = sigStartY + 5;
    
    if (includeDigitalSignatures && staffRoster.length > 0) {
      // Show digital signatures for staff with signatures
      staffRoster.filter(s => s.signature).forEach((staff, idx) => {
        doc.setFontSize(7);
        doc.text(`${staff.symbol}`, 14, sigY + 6);
        doc.setFontSize(10);
        doc.setFont("helvetica", "italic");
        doc.text(staff.signature, 22, sigY + 6);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        doc.text(`(${staff.name})`, 70, sigY + 6);
        sigY += 8;
      });
      if (staffRoster.filter(s => s.signature).length > 0) {
        doc.setFontSize(7);
        doc.text(`Date: ${format(new Date(), "MM/dd/yyyy")}`, 14, sigY + 3);
        sigY += 8;
      }
    } else {
      // Blank signature lines for printing (3 lines)
      for (let i = 0; i < 3; i++) {
        doc.setFontSize(7);
        doc.text("Symbol: ___", 14, sigY + 6);
        doc.line(40, sigY + 6, 100, sigY + 6);
        doc.text("Print Name: ________________________", 105, sigY + 6);
        sigY += 10;
      }
      doc.text("Date: ______________", 14, sigY + 3);
      sigY += 8;
    }
    
    // Director Signature
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text("APPROVED BY (Director):", 14, sigY + 5);
    doc.setFont("helvetica", "normal");
    
    if (includeDigitalSignatures && directorName) {
      doc.setFontSize(8);
      doc.text(`Name: ${directorName}`, 14, sigY + 12);
      if (directorSignature) {
        doc.setFontSize(11);
        doc.setFont("helvetica", "italic");
        doc.text(directorSignature, 14, sigY + 20);
        doc.setFont("helvetica", "normal");
      }
      doc.setFontSize(7);
      doc.text(`Date: ${format(new Date(), "MM/dd/yyyy")}`, 14, sigY + 26);
    } else {
      doc.setFontSize(7);
      doc.text("Name: ________________________________", 14, sigY + 12);
      doc.line(14, sigY + 22, 100, sigY + 22);
      doc.text("Signature", 14, sigY + 26);
      doc.text("Date: ______________", 70, sigY + 26);
    }
    
    doc.save(`CACFP_${format(currentWeekStart, "yyyy-MM-dd")}.pdf`);
    toast.success("PDF exported");
  };
  
  // Helper function to add signature section to any PDF
  const addSignatureSection = (doc, startY) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    let currentY = startY;
    
    doc.setDrawColor(100);
    doc.setLineWidth(0.3);
    
    // Staff Reference Legend (if staff roster exists)
    if (staffRoster.length > 0) {
      doc.setFillColor(250, 250, 255);
      doc.roundedRect(14, currentY, pageWidth - 28, 15, 2, 2, 'F');
      doc.setDrawColor(100, 100, 200);
      doc.roundedRect(14, currentY, pageWidth - 28, 15, 2, 2, 'S');
      
      doc.setFontSize(8);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(50, 50, 150);
      doc.text("STAFF REFERENCE:", 20, currentY + 6);
      
      doc.setFont("helvetica", "normal");
      doc.setTextColor(0, 0, 0);
      const legendText = staffRoster.map(s => `${s.symbol} = ${s.name}`).join('   |   ');
      doc.text(legendText, 20, currentY + 12);
      
      currentY += 18;
    }
    
    // Certification header
    doc.setFillColor(245, 247, 250);
    const boxHeight = staffRoster.length > 0 ? 55 : 40;
    doc.roundedRect(14, currentY, pageWidth - 28, boxHeight, 2, 2, 'F');
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(55, 65, 81);
    doc.text("CERTIFICATION", 20, currentY + 8);
    
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.text("I certify that the above information is true and correct to the best of my knowledge.", 20, currentY + 14);
    
    // Staff Signatures (Multiple)
    const col1X = 20;
    const col2X = pageWidth / 2 + 10;
    let rowY = currentY + 20;
    
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text("Prepared By (Staff):", col1X, rowY);
    doc.setFont("helvetica", "normal");
    
    rowY += 5;
    
    if (includeDigitalSignatures && staffRoster.length > 0) {
      // Show all staff with signatures
      const staffWithSigs = staffRoster.filter(s => s.signature);
      if (staffWithSigs.length > 0) {
        staffWithSigs.forEach((staff, idx) => {
          doc.setFontSize(7);
          doc.text(`${staff.symbol}`, col1X, rowY + 5);
          doc.setFontSize(10);
          doc.setFont("helvetica", "italic");
          doc.text(staff.signature, col1X + 8, rowY + 5);
          doc.setFont("helvetica", "normal");
          doc.setFontSize(6);
          doc.text(`(${staff.name})`, col1X + 50, rowY + 5);
          rowY += 7;
        });
        doc.setFontSize(6);
        doc.text(`Date: ${format(new Date(), "MM/dd/yyyy")}`, col1X, rowY + 2);
      } else {
        // No signatures yet, show blank lines
        for (let i = 0; i < 2; i++) {
          doc.line(col1X, rowY + 5, col1X + 60, rowY + 5);
          rowY += 8;
        }
        doc.setFontSize(6);
        doc.text("Date: ____________", col1X, rowY);
      }
    } else {
      // Blank signature lines for printing
      for (let i = 0; i < 2; i++) {
        doc.setFontSize(6);
        doc.text("___", col1X, rowY + 5);
        doc.line(col1X + 8, rowY + 5, col1X + 60, rowY + 5);
        rowY += 8;
      }
      doc.text("Date: ____________", col1X, rowY);
    }
    
    // Director Signature Column
    rowY = currentY + 20;
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text("Approved By (Director):", col2X, rowY);
    doc.setFont("helvetica", "normal");
    
    rowY += 5;
    
    if (includeDigitalSignatures && directorName) {
      doc.setFontSize(7);
      doc.text(`${directorName}`, col2X, rowY + 5);
      if (directorSignature) {
        doc.setFontSize(10);
        doc.setFont("helvetica", "italic");
        doc.text(directorSignature, col2X, rowY + 12);
      }
      doc.setFontSize(6);
      doc.setFont("helvetica", "normal");
      doc.text(`Date: ${format(new Date(), "MM/dd/yyyy")}`, col2X, rowY + 18);
    } else {
      doc.setFontSize(6);
      doc.text("Name: _________________________", col2X, rowY + 5);
      doc.line(col2X, rowY + 13, col2X + 60, rowY + 13);
      doc.text("Signature", col2X, rowY + 17);
      doc.text("Date: ____________", col2X + 35, rowY + 17);
    }
    
    return currentY + boxHeight + (staffRoster.length > 0 ? 18 : 0);
  };
  
  // Generate Monthly or Yearly Report PDF organized by weeks
  const generateReport = async () => {
    setGeneratingReport(true);
    const orgName = organization?.name || "Childcare Center";
    
    try {
      // Determine date range based on report type
      let startDate, endDate, periodLabel;
      
      if (reportType === "month") {
        startDate = startOfMonth(new Date(reportYear, reportMonth, 1));
        endDate = endOfMonth(new Date(reportYear, reportMonth, 1));
        periodLabel = format(startDate, "MMMM yyyy");
      } else {
        startDate = startOfYear(new Date(reportYear, 0, 1));
        endDate = endOfYear(new Date(reportYear, 0, 1));
        periodLabel = `Year ${reportYear}`;
      }
      
      // Get all weeks in the date range
      const weeksInRange = eachWeekOfInterval(
        { start: startDate, end: endDate },
        { weekStartsOn: 1 }
      );
      
      // Fetch records for the entire date range
      const startStr = format(startDate, "yyyy-MM-dd");
      const endStr = format(endDate, "yyyy-MM-dd");
      
      const res = await axios.get(`${API}/cacfp/records?start_date=${startStr}&end_date=${endStr}`);
      const allRecords = {};
      (res.data?.records || []).forEach(r => {
        const key = `${r.childId}_${r.date}`;
        allRecords[key] = r;
      });
      
      // Create PDF
      const doc = new jsPDF({ orientation: "landscape" });
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Title page / Header
      doc.setFillColor(34, 100, 34);
      doc.rect(0, 0, pageWidth, 35, 'F');
      
      doc.setFontSize(20);
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.text(orgName, pageWidth / 2, 15, { align: "center" });
      
      doc.setFontSize(14);
      doc.text("CACFP MEAL COUNT & ATTENDANCE REPORT", pageWidth / 2, 24, { align: "center" });
      
      doc.setFontSize(11);
      doc.text(periodLabel, pageWidth / 2, 32, { align: "center" });
      
      // Report summary info
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(9);
      doc.text(`Report Generated: ${format(new Date(), "MMMM d, yyyy h:mm a")}`, 14, 45);
      doc.text(`Total Children: ${children.length}`, 14, 51);
      doc.text(`Weeks in Report: ${weeksInRange.length}`, 14, 57);
      
      // Meal Service Address
      if (mealServiceAddress) {
        doc.setFont("helvetica", "bold");
        doc.text(`Meals Served At: ${mealServiceAddress}`, 14, 63);
        doc.setFont("helvetica", "normal");
      }
      
      // Legend
      doc.setFontSize(8);
      doc.text("Legend: BK=Breakfast, AM=AM Snack, L=Lunch, PM=PM Snack, S=Supper | ATT: P=Present, A=Absent, N=Not Scheduled", 14, mealServiceAddress ? 71 : 65);
      
      // Grand totals for entire period
      let grandTotals = { breakfast: 0, amSnack: 0, lunch: 0, pmSnack: 0, supper: 0, present: 0, absent: 0 };
      
      // Weekly summary data for the summary table
      const weeklySummaries = [];
      
      let currentY = mealServiceAddress ? 81 : 75;
      
      // Process each week
      weeksInRange.forEach((weekStart, weekIndex) => {
        const weekEnd = addDays(weekStart, 6);
        const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
        
        // Only include days that fall within our report range
        const relevantDays = weekDays.filter(day => day >= startDate && day <= endDate);
        if (relevantDays.length === 0) return;
        
        // Calculate weekly totals for this week
        let weeklyTotals = { breakfast: 0, amSnack: 0, lunch: 0, pmSnack: 0, supper: 0, present: 0, absent: 0 };
        
        relevantDays.forEach(day => {
          const dateStr = format(day, "yyyy-MM-dd");
          children.forEach(child => {
            const record = allRecords[`${child.id}_${dateStr}`] || {};
            if (record.breakfast) weeklyTotals.breakfast++;
            if (record.amSnack) weeklyTotals.amSnack++;
            if (record.lunch) weeklyTotals.lunch++;
            if (record.pmSnack) weeklyTotals.pmSnack++;
            if (record.supper) weeklyTotals.supper++;
            if (record.attendance === "P") weeklyTotals.present++;
            if (record.attendance === "A") weeklyTotals.absent++;
          });
        });
        
        // Add to grand totals
        grandTotals.breakfast += weeklyTotals.breakfast;
        grandTotals.amSnack += weeklyTotals.amSnack;
        grandTotals.lunch += weeklyTotals.lunch;
        grandTotals.pmSnack += weeklyTotals.pmSnack;
        grandTotals.supper += weeklyTotals.supper;
        grandTotals.present += weeklyTotals.present;
        grandTotals.absent += weeklyTotals.absent;
        
        weeklySummaries.push({
          week: `Week ${weekIndex + 1}: ${format(weekStart, "MMM d")} - ${format(weekEnd, "MMM d")}`,
          ...weeklyTotals,
          totalMeals: weeklyTotals.breakfast + weeklyTotals.amSnack + weeklyTotals.lunch + weeklyTotals.pmSnack + weeklyTotals.supper
        });
      });
      
      // Summary table by week
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(34, 100, 34);
      doc.text("WEEKLY SUMMARY", 14, currentY);
      
      const summaryHeaders = [["Week", "BK", "AM", "L", "PM", "S", "Total Meals", "Present", "Absent"]];
      const summaryData = weeklySummaries.map(ws => [
        ws.week,
        ws.breakfast,
        ws.amSnack,
        ws.lunch,
        ws.pmSnack,
        ws.supper,
        ws.totalMeals,
        ws.present,
        ws.absent
      ]);
      
      // Add grand totals row
      summaryData.push([
        "GRAND TOTAL",
        grandTotals.breakfast,
        grandTotals.amSnack,
        grandTotals.lunch,
        grandTotals.pmSnack,
        grandTotals.supper,
        grandTotals.breakfast + grandTotals.amSnack + grandTotals.lunch + grandTotals.pmSnack + grandTotals.supper,
        grandTotals.present,
        grandTotals.absent
      ]);
      
      autoTable(doc, {
        startY: currentY + 5,
        head: summaryHeaders,
        body: summaryData,
        headStyles: {
          fillColor: [34, 139, 34],
          textColor: [255, 255, 255],
          fontSize: 9,
          fontStyle: "bold"
        },
        styles: {
          fontSize: 8,
          cellPadding: 2
        },
        columnStyles: {
          0: { cellWidth: 55 },
          1: { halign: 'center' },
          2: { halign: 'center' },
          3: { halign: 'center' },
          4: { halign: 'center' },
          5: { halign: 'center' },
          6: { halign: 'center', fontStyle: 'bold' },
          7: { halign: 'center' },
          8: { halign: 'center' }
        },
        didParseCell: function(data) {
          // Style the grand totals row
          if (data.row.index === summaryData.length - 1) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [209, 250, 229];
          }
        }
      });
      
      currentY = doc.lastAutoTable.finalY + 15;
      
      // Grand totals summary box
      doc.setFillColor(240, 253, 244);
      doc.roundedRect(14, currentY, pageWidth - 28, 30, 3, 3, 'F');
      
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(34, 100, 34);
      doc.text(`PERIOD TOTALS - ${periodLabel}`, 20, currentY + 10);
      
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(0, 0, 0);
      const totalMeals = grandTotals.breakfast + grandTotals.amSnack + grandTotals.lunch + grandTotals.pmSnack + grandTotals.supper;
      doc.text(`Total Meals Served: ${totalMeals}`, 20, currentY + 18);
      doc.text(`Breakfast: ${grandTotals.breakfast}  |  AM Snack: ${grandTotals.amSnack}  |  Lunch: ${grandTotals.lunch}  |  PM Snack: ${grandTotals.pmSnack}  |  Supper: ${grandTotals.supper}`, 20, currentY + 25);
      doc.text(`Total Present Days: ${grandTotals.present}  |  Total Absent Days: ${grandTotals.absent}`, pageWidth / 2, currentY + 18);
      
      // Add detailed weekly breakdown pages (optional - one page per week with child details)
      weeksInRange.forEach((weekStart, weekIndex) => {
        const weekEnd = addDays(weekStart, 6);
        const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
        const relevantDays = weekDays.filter(day => day >= startDate && day <= endDate);
        if (relevantDays.length === 0) return;
        
        doc.addPage("landscape");
        
        // Week header
        doc.setFillColor(34, 100, 34);
        doc.rect(0, 0, pageWidth, 25, 'F');
        
        doc.setFontSize(14);
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.text(`Week ${weekIndex + 1}: ${format(weekStart, "MMMM d")} - ${format(weekEnd, "MMMM d, yyyy")}`, pageWidth / 2, 15, { align: "center" });
        
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text(orgName, 14, 35);
        
        // Build table for this week
        const headers = [["#", "PARTICIPANT NAME"]];
        relevantDays.forEach(day => {
          headers[0].push(format(day, "EEE M/d"));
        });
        headers[0].push("BK", "AM", "L", "PM", "S", "ATT");
        
        const tableData = [];
        
        // Group children by category
        const grouped = {};
        children.forEach(child => {
          const cat = child.category || "Uncategorized";
          if (!grouped[cat]) grouped[cat] = [];
          grouped[cat].push(child);
        });
        
        // Sort categories and children
        const categoryOrder = ["full_time", "part_time", "school_age", "after_school", "Uncategorized"];
        const sortedCategories = Object.keys(grouped).sort((a, b) => {
          const aIdx = categoryOrder.indexOf(a);
          const bIdx = categoryOrder.indexOf(b);
          return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx);
        });
        
        let rowNum = 1;
        sortedCategories.forEach(cat => {
          const catChildren = grouped[cat].sort((a, b) => a.name.localeCompare(b.name));
          catChildren.forEach(child => {
            const row = [rowNum++, child.name];
            let childTotals = { breakfast: 0, amSnack: 0, lunch: 0, pmSnack: 0, supper: 0, present: 0, absent: 0 };
            
            relevantDays.forEach(day => {
              const dateStr = format(day, "yyyy-MM-dd");
              const record = allRecords[`${child.id}_${dateStr}`] || {};
              const dayMeals = [];
              // Include staff symbols in the meal display
              if (record.breakfast) { dayMeals.push(`BK${record.enteredBy?.breakfast || ''}`); childTotals.breakfast++; }
              if (record.amSnack) { dayMeals.push(`AM${record.enteredBy?.amSnack || ''}`); childTotals.amSnack++; }
              if (record.lunch) { dayMeals.push(`L${record.enteredBy?.lunch || ''}`); childTotals.lunch++; }
              if (record.pmSnack) { dayMeals.push(`PM${record.enteredBy?.pmSnack || ''}`); childTotals.pmSnack++; }
              if (record.supper) { dayMeals.push(`S${record.enteredBy?.supper || ''}`); childTotals.supper++; }
              if (record.attendance === "P") childTotals.present++;
              if (record.attendance === "A") childTotals.absent++;
              
              row.push(dayMeals.length > 0 ? dayMeals.join(",") : (record.attendance || "-"));
            });
            
            // Add child's weekly totals
            row.push(childTotals.breakfast, childTotals.amSnack, childTotals.lunch, childTotals.pmSnack, childTotals.supper);
            row.push(`P:${childTotals.present}`);
            
            tableData.push(row);
          });
        });
        
        autoTable(doc, {
          startY: 40,
          head: headers,
          body: tableData,
          headStyles: {
            fillColor: [34, 139, 34],
            textColor: [255, 255, 255],
            fontSize: 7,
            fontStyle: "bold"
          },
          styles: {
            fontSize: 7,
            cellPadding: 1.5
          },
          columnStyles: {
            0: { cellWidth: 8 },
            1: { cellWidth: 32 }
          }
        });
      });
      
      // Add signature section on the last page
      const pageHeight = doc.internal.pageSize.getHeight();
      const lastTableY = doc.lastAutoTable?.finalY || 150;
      
      // Check if there's room for signature, if not add new page
      if (lastTableY > pageHeight - 60) {
        doc.addPage("landscape");
        addSignatureSection(doc, 20);
      } else {
        addSignatureSection(doc, lastTableY + 10);
      }
      
      // Footer on last page
      doc.setFontSize(8);
      doc.setTextColor(120);
      doc.text(`${orgName} • CACFP Report • ${periodLabel}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: "center" });
      
      // Save the PDF
      const filename = reportType === "month" 
        ? `CACFP_Report_${format(startDate, "yyyy-MM")}.pdf`
        : `CACFP_Report_${reportYear}.pdf`;
      doc.save(filename);
      
      toast.success(`${reportType === "month" ? "Monthly" : "Yearly"} report exported!`);
      setShowReportDialog(false);
      
    } catch (error) {
      console.error("Error generating report:", error);
      toast.error("Failed to generate report");
    }
    
    setGeneratingReport(false);
  };

  // Generate Month End or Year End Summary Report
  const generateSummaryReport = async () => {
    setGeneratingReport(true);
    const orgName = organization?.name || "Childcare Center";
    const isMonthly = reportType === "monthSummary";
    
    try {
      // Determine date range
      let startDate, endDate, periodLabel;
      
      if (isMonthly) {
        startDate = startOfMonth(new Date(reportYear, reportMonth, 1));
        endDate = endOfMonth(new Date(reportYear, reportMonth, 1));
        periodLabel = format(startDate, "MMMM yyyy");
      } else {
        startDate = startOfYear(new Date(reportYear, 0, 1));
        endDate = endOfYear(new Date(reportYear, 0, 1));
        periodLabel = `Year ${reportYear}`;
      }
      
      // Fetch all records for the period
      const startStr = format(startDate, "yyyy-MM-dd");
      const endStr = format(endDate, "yyyy-MM-dd");
      
      const res = await axios.get(`${API}/cacfp/records?start_date=${startStr}&end_date=${endStr}`);
      const allRecords = {};
      (res.data?.records || []).forEach(r => {
        const key = `${r.childId}_${r.date}`;
        allRecords[key] = r;
      });
      
      // Calculate per-child totals
      const childTotals = children.map(child => {
        const totals = { 
          name: child.name, 
          category: child.category || "Uncategorized",
          breakfast: 0, amSnack: 0, lunch: 0, pmSnack: 0, supper: 0, 
          present: 0, absent: 0, totalMeals: 0 
        };
        
        // Loop through all days in the period
        let currentDate = new Date(startDate);
        while (currentDate <= endDate) {
          const dateStr = format(currentDate, "yyyy-MM-dd");
          const record = allRecords[`${child.id}_${dateStr}`] || {};
          
          if (record.breakfast) totals.breakfast++;
          if (record.amSnack) totals.amSnack++;
          if (record.lunch) totals.lunch++;
          if (record.pmSnack) totals.pmSnack++;
          if (record.supper) totals.supper++;
          if (record.attendance === "P") totals.present++;
          if (record.attendance === "A") totals.absent++;
          
          currentDate = addDays(currentDate, 1);
        }
        
        totals.totalMeals = totals.breakfast + totals.amSnack + totals.lunch + totals.pmSnack + totals.supper;
        return totals;
      });
      
      // Calculate grand totals
      const grandTotals = childTotals.reduce((acc, child) => {
        acc.breakfast += child.breakfast;
        acc.amSnack += child.amSnack;
        acc.lunch += child.lunch;
        acc.pmSnack += child.pmSnack;
        acc.supper += child.supper;
        acc.present += child.present;
        acc.absent += child.absent;
        acc.totalMeals += child.totalMeals;
        return acc;
      }, { breakfast: 0, amSnack: 0, lunch: 0, pmSnack: 0, supper: 0, present: 0, absent: 0, totalMeals: 0 });
      
      // Create PDF
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Header
      doc.setFillColor(34, 100, 34);
      doc.rect(0, 0, pageWidth, 40, 'F');
      
      doc.setFontSize(20);
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.text(orgName, pageWidth / 2, 15, { align: "center" });
      
      doc.setFontSize(16);
      doc.text(isMonthly ? "MONTH END SUMMARY REPORT" : "YEAR END SUMMARY REPORT", pageWidth / 2, 25, { align: "center" });
      
      doc.setFontSize(12);
      doc.text(periodLabel, pageWidth / 2, 35, { align: "center" });
      
      // Report info
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(9);
      doc.text(`Report Generated: ${format(new Date(), "MMMM d, yyyy h:mm a")}`, 14, 50);
      doc.text(`Total Children Enrolled: ${children.length}`, 14, 56);
      
      // Meal Service Address
      if (mealServiceAddress) {
        doc.setFont("helvetica", "bold");
        doc.text(`Meals Served At: ${mealServiceAddress}`, 14, 62);
        doc.setFont("helvetica", "normal");
      }
      
      // Grand Totals Summary Box
      const boxStartY = mealServiceAddress ? 68 : 62;
      doc.setFillColor(240, 253, 244);
      doc.roundedRect(14, boxStartY, pageWidth - 28, 45, 3, 3, 'F');
      doc.setDrawColor(34, 139, 34);
      doc.setLineWidth(0.5);
      doc.roundedRect(14, boxStartY, pageWidth - 28, 45, 3, 3, 'S');
      
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(34, 100, 34);
      doc.text(`${isMonthly ? "MONTH" : "YEAR"} END TOTALS`, pageWidth / 2, boxStartY + 10, { align: "center" });
      
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "normal");
      
      // Meal totals in a nice layout
      const col1X = 25;
      const col2X = 75;
      const col3X = 125;
      const row1Y = boxStartY + 20;
      const row2Y = boxStartY + 28;
      const row3Y = boxStartY + 38;
      
      doc.text(`Breakfast:`, col1X, row1Y);
      doc.setFont("helvetica", "bold");
      doc.text(`${grandTotals.breakfast}`, col1X + 25, row1Y);
      
      doc.setFont("helvetica", "normal");
      doc.text(`AM Snack:`, col2X, row1Y);
      doc.setFont("helvetica", "bold");
      doc.text(`${grandTotals.amSnack}`, col2X + 25, row1Y);
      
      doc.setFont("helvetica", "normal");
      doc.text(`Lunch:`, col3X, row1Y);
      doc.setFont("helvetica", "bold");
      doc.text(`${grandTotals.lunch}`, col3X + 20, row1Y);
      
      doc.setFont("helvetica", "normal");
      doc.text(`PM Snack:`, col1X, row2Y);
      doc.setFont("helvetica", "bold");
      doc.text(`${grandTotals.pmSnack}`, col1X + 25, row2Y);
      
      doc.setFont("helvetica", "normal");
      doc.text(`Supper:`, col2X, row2Y);
      doc.setFont("helvetica", "bold");
      doc.text(`${grandTotals.supper}`, col2X + 20, row2Y);
      
      doc.setFont("helvetica", "normal");
      doc.text(`Total Meals:`, col3X, row2Y);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(34, 100, 34);
      doc.text(`${grandTotals.totalMeals}`, col3X + 28, row2Y);
      
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "normal");
      doc.text(`Present Days:`, col1X, row3Y);
      doc.setFont("helvetica", "bold");
      doc.text(`${grandTotals.present}`, col1X + 32, row3Y);
      
      doc.setFont("helvetica", "normal");
      doc.text(`Absent Days:`, col2X, row3Y);
      doc.setFont("helvetica", "bold");
      doc.text(`${grandTotals.absent}`, col2X + 30, row3Y);
      
      // Per-Child Summary Table
      const tableHeaderY = boxStartY + 50;
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(34, 100, 34);
      doc.text("INDIVIDUAL CHILD SUMMARY", 14, tableHeaderY);
      
      // Group by category
      const grouped = {};
      childTotals.forEach(child => {
        const cat = child.category;
        if (!grouped[cat]) grouped[cat] = [];
        grouped[cat].push(child);
      });
      
      const categoryOrder = ["full_time", "part_time", "school_age", "after_school", "Uncategorized"];
      const categoryLabels = {
        "full_time": "Full Time",
        "part_time": "Part Time", 
        "school_age": "School Age",
        "after_school": "After School",
        "Uncategorized": "Other"
      };
      
      const sortedCategories = Object.keys(grouped).sort((a, b) => {
        const aIdx = categoryOrder.indexOf(a);
        const bIdx = categoryOrder.indexOf(b);
        return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx);
      });
      
      const tableData = [];
      sortedCategories.forEach(cat => {
        // Add category header row
        tableData.push([{ 
          content: categoryLabels[cat] || cat, 
          colSpan: 9, 
          styles: { fontStyle: 'bold', fillColor: [229, 231, 235], textColor: [55, 65, 81] }
        }]);
        
        // Add children in this category
        grouped[cat].sort((a, b) => a.name.localeCompare(b.name)).forEach(child => {
          tableData.push([
            child.name,
            child.breakfast,
            child.amSnack,
            child.lunch,
            child.pmSnack,
            child.supper,
            child.totalMeals,
            child.present,
            child.absent
          ]);
        });
      });
      
      // Add grand totals row
      tableData.push([
        { content: 'GRAND TOTAL', styles: { fontStyle: 'bold' } },
        { content: grandTotals.breakfast, styles: { fontStyle: 'bold' } },
        { content: grandTotals.amSnack, styles: { fontStyle: 'bold' } },
        { content: grandTotals.lunch, styles: { fontStyle: 'bold' } },
        { content: grandTotals.pmSnack, styles: { fontStyle: 'bold' } },
        { content: grandTotals.supper, styles: { fontStyle: 'bold' } },
        { content: grandTotals.totalMeals, styles: { fontStyle: 'bold', textColor: [34, 100, 34] } },
        { content: grandTotals.present, styles: { fontStyle: 'bold' } },
        { content: grandTotals.absent, styles: { fontStyle: 'bold' } }
      ]);
      
      autoTable(doc, {
        startY: tableHeaderY + 5,
        head: [["Child Name", "BK", "AM", "L", "PM", "S", "Total", "Present", "Absent"]],
        body: tableData,
        headStyles: {
          fillColor: [34, 139, 34],
          textColor: [255, 255, 255],
          fontSize: 9,
          fontStyle: "bold"
        },
        styles: {
          fontSize: 8,
          cellPadding: 2
        },
        columnStyles: {
          0: { cellWidth: 50 },
          1: { halign: 'center', cellWidth: 15 },
          2: { halign: 'center', cellWidth: 15 },
          3: { halign: 'center', cellWidth: 15 },
          4: { halign: 'center', cellWidth: 15 },
          5: { halign: 'center', cellWidth: 15 },
          6: { halign: 'center', cellWidth: 18 },
          7: { halign: 'center', cellWidth: 18 },
          8: { halign: 'center', cellWidth: 18 }
        },
        didParseCell: function(data) {
          // Style the grand totals row
          if (data.row.index === tableData.length - 1) {
            data.cell.styles.fillColor = [209, 250, 229];
          }
        }
      });
      
      // Add signature section
      const tableEndY = doc.lastAutoTable.finalY;
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // Check if there's room for signature section
      if (tableEndY > pageHeight - 70) {
        doc.addPage();
        addSignatureSection(doc, 20);
      } else {
        addSignatureSection(doc, tableEndY + 10);
      }
      
      // Footer
      doc.setFontSize(8);
      doc.setTextColor(120);
      doc.setFont("helvetica", "italic");
      doc.text("This summary report is generated for CACFP compliance and record-keeping purposes.", pageWidth / 2, pageHeight - 15, { align: "center" });
      doc.text(`${orgName} • ${isMonthly ? "Month End" : "Year End"} Summary • ${periodLabel}`, pageWidth / 2, pageHeight - 10, { align: "center" });
      
      // Save
      const filename = isMonthly 
        ? `CACFP_Month_End_Summary_${format(startDate, "yyyy-MM")}.pdf`
        : `CACFP_Year_End_Summary_${reportYear}.pdf`;
      doc.save(filename);
      
      toast.success(`${isMonthly ? "Month End" : "Year End"} Summary exported!`);
      setShowReportDialog(false);
      
    } catch (error) {
      console.error("Error generating summary report:", error);
      toast.error("Failed to generate summary report");
    }
    
    setGeneratingReport(false);
  };

  // Main report handler - routes to correct generator
  const handleGenerateReport = () => {
    if (reportType === "monthSummary" || reportType === "yearSummary") {
      generateSummaryReport();
    } else {
      generateReport();
    }
  };
  
  // Print function
  const handlePrint = () => {
    window.print();
  };
  
  const weeklyTotals = getWeeklyTotals();
  
  return (
    <AppShell pageTitle="Meal Compliance Tracker">
    <div className="bg-gradient-to-br from-green-50 to-emerald-100" data-testid="cacfp-page">
      {/* Hero Banner */}
      <PageHero 
        imageUrl={HERO_IMAGES.cacfp}
        title="Meal Compliance Tracker"
        subtitle="Meal count and attendance management"
        altText="CACFP healthy meals for children"
        imagePosition="center 70%"
      />
      
      <FeatureGate feature="meal_compliance">
      <div className="relative z-10 max-w-full mx-auto p-4">
        {/* Print Header - Only visible when printing */}
        <PrintReportHeader reportTitle="Meal Compliance Tracker" />
        
        {/* Header */}
        <div className="flex items-center justify-between mb-4 print:hidden">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-green-800">Meal Count & Attendance</h1>
              <p className="text-sm text-green-600">{organization?.name || "Childcare Center"}</p>
              {/* Meal Service Address Display */}
              {mealServiceAddress ? (
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {mealServiceAddress}
                  <button 
                    onClick={() => { setTempAddress(mealServiceAddress); setShowAddressDialog(true); }}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                </p>
              ) : (
                <button 
                  onClick={() => { setTempAddress(''); setShowAddressDialog(true); }}
                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  <MapPin className="w-3 h-3" />
                  + Add Meal Service Address
                </button>
              )}
              {/* Active Staff Indicator */}
              {activeStaff && (
                <p className="text-xs text-blue-600 flex items-center gap-1 mt-1">
                  <span className="text-lg leading-none">{activeStaff.symbol}</span>
                  <span>Entering as: <strong>{activeStaff.name}</strong></span>
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {/* Address Edit Dialog */}
            <Dialog open={showAddressDialog} onOpenChange={setShowAddressDialog}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-green-800">
                    <MapPin className="w-5 h-5" />
                    Meal Service Address
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <p className="text-sm text-gray-600">
                    Enter the address where meals are served. This will appear on all CACFP reports for compliance.
                  </p>
                  <div className="space-y-2">
                    <Label className="text-green-700 font-medium">Address</Label>
                    <Textarea
                      value={tempAddress}
                      onChange={(e) => setTempAddress(e.target.value)}
                      placeholder="123 Main Street, City, State 12345"
                      className="border-green-200 min-h-[80px]"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={saveMealServiceAddress}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    >
                      Save Address
                    </Button>
                    <Button 
                      onClick={() => setShowAddressDialog(false)}
                      variant="outline"
                      className="border-gray-300"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            
            {/* Staff Roster Dialog - Only for Owners/Co-Owners */}
            {(user?.role === 'owner' || user?.role?.toLowerCase() === 'co-owner') && (
            <Dialog open={showStaffRosterDialog} onOpenChange={setShowStaffRosterDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50">
                  <Users className="w-4 h-4 mr-2" />
                  Staff Roster ({staffRoster.length})
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-blue-800">
                    <Users className="w-5 h-5" />
                    Staff Roster & Symbols
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <p className="text-sm text-gray-600">
                    Assign unique symbols to each staff member. Symbols will appear on reports to identify who entered the data.
                  </p>
                  
                  {/* Current Staff List */}
                  {staffRoster.length > 0 && (
                    <div className="border rounded-lg divide-y">
                      {staffRoster.map((staff) => (
                        <div key={staff.id} className={`p-3 flex items-center justify-between ${staff.userId === user?.id ? 'bg-green-50' : ''}`}>
                          <div className="flex items-center gap-3">
                            <span className="text-2xl w-8 text-center">{staff.symbol}</span>
                            <div>
                              <p className="font-medium text-gray-800">
                                {staff.name}
                                {staff.userId === user?.id && (
                                  <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">You</span>
                                )}
                              </p>
                              {staff.email && (
                                <p className="text-xs text-gray-400">{staff.email}</p>
                              )}
                              {staff.signature && (
                                <p className="text-xs text-gray-500 italic">Signature: {staff.signature}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Input
                              placeholder="Type signature"
                              value={staff.signature || ''}
                              onChange={(e) => updateStaffSignature(staff.id, e.target.value)}
                              className="w-32 text-sm h-8"
                              style={{ fontStyle: 'italic' }}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1"
                              onClick={() => removeStaffMember(staff.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {staffRoster.length === 0 && (
                    <div className="text-center py-6 bg-gray-50 rounded-lg">
                      <Users className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-500 text-sm">No staff members added yet</p>
                    </div>
                  )}
                  
                  {/* Add New Staff - Select from System Users */}
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm font-medium text-blue-700 mb-2">Add Staff Member</p>
                    <div className="flex gap-2">
                      <Select value={newStaffSymbol} onValueChange={setNewStaffSymbol}>
                        <SelectTrigger className="w-20 border-blue-200 bg-white">
                          <SelectValue placeholder="★" />
                        </SelectTrigger>
                        <SelectContent>
                          {getAvailableSymbols().map(symbol => (
                            <SelectItem key={symbol} value={symbol}>
                              <span className="text-lg">{symbol}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                        <SelectTrigger className="flex-1 border-blue-200 bg-white">
                          <SelectValue placeholder="Select staff member..." />
                        </SelectTrigger>
                        <SelectContent>
                          {getAvailableUsers().map(u => (
                            <SelectItem key={u.id} value={u.id}>
                              <span className="flex items-center gap-2">
                                {u.name || u.email}
                                {u.id === user?.id && <span className="text-xs text-blue-500">(You)</span>}
                              </span>
                            </SelectItem>
                          ))}
                          {getAvailableUsers().length === 0 && (
                            <div className="px-2 py-1 text-sm text-gray-500">
                              All staff members are already in the roster
                            </div>
                          )}
                        </SelectContent>
                      </Select>
                      <Button
                        onClick={addStaffMember}
                        disabled={!selectedUserId || !newStaffSymbol}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    {getAvailableSymbols().length === 0 && (
                      <p className="text-xs text-orange-600 mt-1">All symbols are assigned</p>
                    )}
                    {getAvailableUsers().length === 0 && staffRoster.length > 0 && (
                      <p className="text-xs text-green-600 mt-1">All staff members have been assigned symbols</p>
                    )}
                  </div>
                  
                  {/* Active Staff Selector */}
                  {staffRoster.length > 0 && (
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-sm font-medium text-green-700 mb-2">Currently Entering Data As:</p>
                      <Select 
                        value={activeStaff?.id || ''} 
                        onValueChange={(id) => setCurrentStaff(staffRoster.find(s => s.id === id))}
                      >
                        <SelectTrigger className="border-green-200 bg-white">
                          <SelectValue placeholder="Select your name..." />
                        </SelectTrigger>
                        <SelectContent>
                          {staffRoster.map(staff => (
                            <SelectItem key={staff.id} value={staff.id}>
                              <span className="flex items-center gap-2">
                                <span className="text-lg">{staff.symbol}</span>
                                {staff.name}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {activeStaff && (
                        <p className="text-xs text-green-600 mt-1">
                          Your entries will be marked with: <span className="text-lg">{activeStaff.symbol}</span>
                        </p>
                      )}
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    {staffRoster.length > 0 && (
                      <Button 
                        onClick={printStaffRoster}
                        variant="outline"
                        className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-50"
                      >
                        <Printer className="w-4 h-4 mr-2" />
                        Print Roster
                      </Button>
                    )}
                    <Button 
                      onClick={() => setShowStaffRosterDialog(false)}
                      className={`${staffRoster.length > 0 ? 'flex-1' : 'w-full'} bg-blue-600 hover:bg-blue-700 text-white`}
                    >
                      Done
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            )}
            
            {/* Signature Management Dialog */}
            <Dialog open={showSignatureDialog} onOpenChange={setShowSignatureDialog}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline" 
                  className="border-purple-300 text-purple-700 hover:bg-purple-50"
                  onClick={() => {
                    setTempStaffSig(staffSignature);
                    setTempDirName(directorName);
                    setTempDirSig(directorSignature);
                  }}
                >
                  <PenLine className="w-4 h-4 mr-2" />
                  Signatures
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-purple-800">
                    <PenLine className="w-5 h-5" />
                    Report Signatures (CACFP Compliance)
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <p className="text-sm text-gray-600">
                    Set up digital signatures for CACFP reports. These will appear on all exported reports. 
                    Leave blank if you prefer to print and sign manually.
                  </p>
                  
                  {/* Staff Signature */}
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <Label className="text-blue-700 font-medium">Staff Signature (Prepared By)</Label>
                    <p className="text-xs text-blue-600 mb-2">Type your name as a digital signature</p>
                    <Input
                      value={tempStaffSig}
                      onChange={(e) => setTempStaffSig(e.target.value)}
                      placeholder="e.g., John Smith"
                      className="border-blue-200 bg-white font-italic"
                      style={{ fontStyle: 'italic' }}
                    />
                    {tempStaffSig && (
                      <p className="mt-2 text-lg italic text-blue-800 border-b border-blue-300 pb-1">
                        {tempStaffSig}
                      </p>
                    )}
                  </div>
                  
                  {/* Director Section */}
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <Label className="text-green-700 font-medium">Director Information (Approved By)</Label>
                    <p className="text-xs text-green-600 mb-2">Enter director's name and signature</p>
                    
                    <div className="space-y-2">
                      <Input
                        value={tempDirName}
                        onChange={(e) => setTempDirName(e.target.value)}
                        placeholder="Director's Full Name"
                        className="border-green-200 bg-white"
                      />
                      <Input
                        value={tempDirSig}
                        onChange={(e) => setTempDirSig(e.target.value)}
                        placeholder="Director's Signature (type name)"
                        className="border-green-200 bg-white"
                        style={{ fontStyle: 'italic' }}
                      />
                    </div>
                    {tempDirSig && (
                      <p className="mt-2 text-lg italic text-green-800 border-b border-green-300 pb-1">
                        {tempDirSig}
                      </p>
                    )}
                  </div>
                  
                  {/* Include Digital Signatures Toggle */}
                  <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                    <input
                      type="checkbox"
                      id="includeDigitalSigs"
                      checked={includeDigitalSignatures}
                      onChange={(e) => setIncludeDigitalSignatures(e.target.checked)}
                      className="w-4 h-4 accent-purple-600"
                    />
                    <label htmlFor="includeDigitalSigs" className="text-sm text-gray-700">
                      Include digital signatures on reports (uncheck to print blank signature lines)
                    </label>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      onClick={saveSignatures}
                      className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      Save Signatures
                    </Button>
                    <Button 
                      onClick={() => setShowSignatureDialog(false)}
                      variant="outline"
                      className="border-gray-300"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            
            <Button onClick={syncChildren} disabled={syncing} variant="outline" className="border-green-300 text-green-700">
              <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              Sync Children
            </Button>
            <Button onClick={handlePrint} variant="outline" className="border-amber-300 text-amber-700">
              <Printer className="w-4 h-4 mr-2" />Print
            </Button>
            <Button onClick={exportPDF} variant="outline" className="border-red-300 text-red-700">
              <FileSpreadsheet className="w-4 h-4 mr-2" />Export PDF
            </Button>
            
            {/* Report Generator Dialog */}
            <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100">
                  <CalendarRange className="w-4 h-4 mr-2" />
                  Generate Report
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-green-800">
                    <FileText className="w-5 h-5" />
                    Generate CACFP Report
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <p className="text-sm text-gray-600">
                    Generate meal count and attendance reports for CACFP compliance.
                  </p>
                  
                  {/* Report Type Selection */}
                  <div className="space-y-2">
                    <Label className="text-green-700 font-medium">Report Type</Label>
                    <Select value={reportType} onValueChange={setReportType}>
                      <SelectTrigger className="border-green-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="month">Monthly Detail (by weeks)</SelectItem>
                        <SelectItem value="year">Yearly Detail (by weeks)</SelectItem>
                        <SelectItem value="monthSummary">Month End Summary</SelectItem>
                        <SelectItem value="yearSummary">Year End Summary</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Month Selection (if monthly report) */}
                  {(reportType === "month" || reportType === "monthSummary") && (
                    <div className="space-y-2">
                      <Label className="text-green-700 font-medium">Select Month</Label>
                      <Select value={reportMonth.toString()} onValueChange={(v) => setReportMonth(parseInt(v))}>
                        <SelectTrigger className="border-green-200">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 12 }, (_, i) => (
                            <SelectItem key={i} value={i.toString()}>
                              {format(new Date(2024, i, 1), "MMMM")}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {/* Year Selection */}
                  <div className="space-y-2">
                    <Label className="text-green-700 font-medium">Select Year</Label>
                    <Select value={reportYear.toString()} onValueChange={(v) => setReportYear(parseInt(v))}>
                      <SelectTrigger className="border-green-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 5 }, (_, i) => {
                          const year = new Date().getFullYear() - 2 + i;
                          return (
                            <SelectItem key={year} value={year.toString()}>
                              {year}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Report Preview Info - Different for each type */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-green-800 mb-1">
                      {reportType === "monthSummary" || reportType === "yearSummary" 
                        ? "Summary Report includes:" 
                        : "Detailed Report includes:"}
                    </p>
                    {(reportType === "month" || reportType === "year") ? (
                      <ul className="text-xs text-green-600 space-y-1">
                        <li>• Weekly summary table with meal counts</li>
                        <li>• Grand totals for the entire period</li>
                        <li>• Detailed weekly breakdown pages</li>
                        <li>• Children grouped by category</li>
                        <li>• Attendance tracking (Present/Absent)</li>
                      </ul>
                    ) : (
                      <ul className="text-xs text-green-600 space-y-1">
                        <li>• Period totals summary box</li>
                        <li>• Per-child meal counts (BK, AM, L, PM, S)</li>
                        <li>• Total meals per child</li>
                        <li>• Attendance summary per child</li>
                        <li>• Grand totals for all categories</li>
                        <li>• Clean single-page format for records</li>
                      </ul>
                    )}
                  </div>
                  
                  {/* Generate Button */}
                  <Button 
                    onClick={handleGenerateReport} 
                    disabled={generatingReport}
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                  >
                    {generatingReport ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Generating Report...
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4 mr-2" />
                        Generate {
                          reportType === "month" ? "Monthly Detail" : 
                          reportType === "year" ? "Yearly Detail" :
                          reportType === "monthSummary" ? "Month End Summary" :
                          "Year End Summary"
                        } PDF
                      </>
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
        
        {/* Week Navigation */}
        <Card className="mb-4 bg-white/90 border-green-200">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <Button variant="ghost" onClick={() => navigateWeek("prev")} className="text-green-700">
                <ChevronLeft className="w-5 h-5 mr-1" />Previous Week
              </Button>
              <div className="text-center">
                <p className="text-lg font-semibold text-green-800">
                  Week of {format(currentWeekStart, "MMMM d, yyyy")}
                </p>
                <p className="text-sm text-green-600">
                  {format(currentWeekStart, "MMM d")} - {format(addDays(currentWeekStart, 6), "MMM d, yyyy")}
                </p>
              </div>
              <Button variant="ghost" onClick={() => navigateWeek("next")} className="text-green-700">
                Next Week<ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* CACFP Compliance Notice - Attendance Required */}
        <div className="mb-4 bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-red-500 rounded-r-lg p-3 print:hidden">
          <div className="flex items-start gap-3">
            <div className="bg-red-500 text-white rounded-full p-1.5 mt-0.5">
              <AlertTriangle className="w-4 h-4" />
            </div>
            <div>
              <p className="font-bold text-red-800 text-sm">CACFP Compliance Requirement</p>
              <p className="text-red-700 text-xs mt-1">
                Attendance must be recorded before meal counts. Meal entry is disabled until a child is marked <strong>Present (P)</strong>. 
                This ensures accurate documentation for CACFP inspections.
              </p>
            </div>
          </div>
        </div>
        
        {/* Legend */}
        <div className="mb-4 flex flex-wrap gap-4 text-xs print:hidden">
          <div className="bg-white/80 rounded-lg px-3 py-2 border border-green-200">
            <span className="font-semibold text-green-700">Attendance:</span>
            <span className="ml-2">A=Absent, P=Present, N=Not Scheduled</span>
          </div>
          <div className="bg-white/80 rounded-lg px-3 py-2 border border-green-200">
            <span className="font-semibold text-green-700">Meals:</span>
            <span className="ml-2">BK=Breakfast, AM=AM Snack, L=Lunch, PM=PM Snack, S=Supper</span>
          </div>
          <div className="bg-white/80 rounded-lg px-3 py-2 border border-green-200">
            <span className="font-semibold text-green-700">Click/Tap</span>
            <span className="ml-2">to mark {activeStaff?.symbol || 'X'} for Meal Count. {!activeStaff && '(X = no staff symbol assigned)'} Mark child as Present (P) first.</span>
          </div>
          {/* Active Staff Selector - Inline */}
          {staffRoster.length > 0 && (
            <div className="bg-blue-50 rounded-lg px-3 py-2 border border-blue-200 flex items-center gap-2">
              <span className="font-semibold text-blue-700">Recording as:</span>
              <Select 
                value={activeStaff?.id || ''} 
                onValueChange={(id) => setCurrentStaff(staffRoster.find(s => s.id === id))}
              >
                <SelectTrigger className="h-7 w-40 border-blue-200 bg-white text-sm">
                  <SelectValue placeholder="Select staff..." />
                </SelectTrigger>
                <SelectContent>
                  {staffRoster.map(staff => (
                    <SelectItem key={staff.id} value={staff.id}>
                      <span className="flex items-center gap-2">
                        <span className="text-lg">{staff.symbol}</span>
                        {staff.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {activeStaff && (
                <span className="text-lg" title={activeStaff.name}>{activeStaff.symbol}</span>
              )}
            </div>
          )}
          {/* Message for Owners/Co-Owners when no staff in roster */}
          {staffRoster.length === 0 && (user?.role === 'owner' || user?.role?.toLowerCase() === 'co-owner') && (
            <div className="bg-yellow-50 rounded-lg px-3 py-2 border border-yellow-200 flex items-center gap-2 text-yellow-700">
              <Users className="w-4 h-4" />
              <span>Add staff in Staff Roster to assign symbols for tracking meal entries</span>
            </div>
          )}
          {/* Message for Staff when no symbol assigned */}
          {staffRoster.length === 0 && user?.role === 'staff' && (
            <div className="bg-blue-50 rounded-lg px-3 py-2 border border-blue-200 flex items-center gap-2 text-blue-700">
              <Users className="w-4 h-4" />
              <span>Your entries will show as "X". Contact your director to be assigned a unique symbol for tracking.</span>
            </div>
          )}
          {/* Message for Staff when roster exists but they're not in it */}
          {staffRoster.length > 0 && user?.role === 'staff' && !staffRoster.find(s => s.userId === user?.id) && (
            <div className="bg-orange-50 rounded-lg px-3 py-2 border border-orange-200 flex items-center gap-2 text-orange-700">
              <Users className="w-4 h-4" />
              <span>You haven't been assigned a symbol yet. Ask your director to add you to the Staff Roster.</span>
            </div>
          )}
        </div>
        
        {/* Main Table */}
        <Card className="bg-white/95 border-green-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                {/* Day Headers */}
                <tr className="bg-green-600 text-white">
                  <th className="border border-green-500 p-2 sticky left-0 bg-green-600 z-10 w-8">#</th>
                  <th className="border border-green-500 p-2 sticky left-8 bg-green-600 z-10 min-w-[150px] text-left">
                    PARTICIPANT NAME
                  </th>
                  {weekDays.map(day => (
                    <th key={day.toISOString()} colSpan={6} className="border border-green-500 p-2 text-center">
                      <div>{format(day, "EEEE")}</div>
                      <div className="text-xs font-normal">{format(day, "M/d")}</div>
                    </th>
                  ))}
                </tr>
                {/* Meal Type Headers */}
                <tr className="bg-green-500 text-white text-xs">
                  <th className="border border-green-400 p-1 sticky left-0 bg-green-500 z-10"></th>
                  <th className="border border-green-400 p-1 sticky left-8 bg-green-500 z-10"></th>
                  {weekDays.map(day => (
                    <React.Fragment key={`meals-${day.toISOString()}`}>
                      <th className="border border-green-400 p-1 w-8">BK</th>
                      <th className="border border-green-400 p-1 w-8">AM</th>
                      <th className="border border-green-400 p-1 w-8">L</th>
                      <th className="border border-green-400 p-1 w-8">PM</th>
                      <th className="border border-green-400 p-1 w-8">S</th>
                      <th className="border border-green-400 p-1 w-12">ATT</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={2 + weekDays.length * 6} className="p-8 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : children.length === 0 ? (
                  <tr>
                    <td colSpan={2 + weekDays.length * 6} className="p-8 text-center text-gray-500">
                      No children found. Click "Sync Children" to load.
                    </td>
                  </tr>
                ) : (
                  <>
                    {["full_time", "part_time", "school_age", "after_school", "other"].map(categoryKey => {
                      const categoryChildren = groupedChildren[categoryKey];
                      if (categoryChildren.length === 0) return null;
                      
                      return (
                        <React.Fragment key={categoryKey}>
                          {/* Category Header Row */}
                          <tr className={`${getCategoryColor(categoryKey)} text-white`}>
                            <td colSpan={2 + weekDays.length * 6} className="p-2 font-bold text-sm sticky left-0 z-10" style={{backgroundColor: 'inherit'}}>
                              {getCategoryLabel(categoryKey)} ({categoryChildren.length})
                            </td>
                          </tr>
                          {/* Children in this category */}
                          {categoryChildren.map((child, idx) => {
                            return (
                              <tr key={child.id} className="hover:bg-green-50">
                                <td className="border border-green-200 p-1 text-center text-gray-500 sticky left-0 bg-white z-10">
                                  {idx + 1}
                                </td>
                                <td className="border border-green-200 p-2 font-medium sticky left-8 bg-white z-10">
                                  {child.name}
                                </td>
                                {weekDays.map(day => {
                                  const record = getRecord(child.id, day);
                                  const isPresent = record.attendance === "P";
                                  const mealCellClass = isPresent 
                                    ? "border border-green-200 p-1 text-center cursor-pointer transition-colors"
                                    : "border border-green-200 p-1 text-center cursor-not-allowed bg-gray-100 opacity-50";
                                  // Get the symbol to display - use enteredBy symbol if exists, otherwise use active staff symbol or X
                                  const getDisplaySymbol = (mealType) => {
                                    if (record.enteredBy?.[mealType]) return record.enteredBy[mealType];
                                    return activeStaff?.symbol || 'X';
                                  };
                                  return (
                                    <React.Fragment key={`${child.id}-${day.toISOString()}`}>
                                      {/* Breakfast */}
                                      <td 
                                        className={`${mealCellClass} ${isPresent ? 'hover:bg-amber-100' : ''}`}
                                        onClick={() => toggleMeal(child.id, day, "breakfast")}
                                        title={!isPresent ? "Mark Present first" : `Click to mark Breakfast${record.enteredBy?.breakfast ? ` (entered by ${record.enteredBy.breakfast})` : ''}`}
                                      >
                                        {record.breakfast && (
                                          <span className="text-amber-600 font-semibold text-base">
                                            {record.enteredBy?.breakfast || 'X'}
                                          </span>
                                        )}
                                      </td>
                                      {/* AM Snack */}
                                      <td 
                                        className={`${mealCellClass} ${isPresent ? 'hover:bg-orange-100' : ''}`}
                                        onClick={() => toggleMeal(child.id, day, "amSnack")}
                                        title={!isPresent ? "Mark Present first" : `Click to mark AM Snack${record.enteredBy?.amSnack ? ` (entered by ${record.enteredBy.amSnack})` : ''}`}
                                      >
                                        {record.amSnack && (
                                          <span className="text-orange-600 font-semibold text-base">
                                            {record.enteredBy?.amSnack || 'X'}
                                          </span>
                                        )}
                                      </td>
                                      {/* Lunch */}
                                      <td 
                                        className={`${mealCellClass} ${isPresent ? 'hover:bg-green-100' : ''}`}
                                        onClick={() => toggleMeal(child.id, day, "lunch")}
                                        title={!isPresent ? "Mark Present first" : `Click to mark Lunch${record.enteredBy?.lunch ? ` (entered by ${record.enteredBy.lunch})` : ''}`}
                                      >
                                        {record.lunch && (
                                          <span className="text-green-600 font-semibold text-base">
                                            {record.enteredBy?.lunch || 'X'}
                                          </span>
                                        )}
                                      </td>
                                      {/* PM Snack */}
                                      <td 
                                        className={`${mealCellClass} ${isPresent ? 'hover:bg-blue-100' : ''}`}
                                        onClick={() => toggleMeal(child.id, day, "pmSnack")}
                                        title={!isPresent ? "Mark Present first" : `Click to mark PM Snack${record.enteredBy?.pmSnack ? ` (entered by ${record.enteredBy.pmSnack})` : ''}`}
                                      >
                                        {record.pmSnack && (
                                          <span className="text-blue-600 font-semibold text-base">
                                            {record.enteredBy?.pmSnack || 'X'}
                                          </span>
                                        )}
                                      </td>
                                      {/* Supper */}
                                      <td 
                                        className={`${mealCellClass} ${isPresent ? 'hover:bg-purple-100' : ''}`}
                                        onClick={() => toggleMeal(child.id, day, "supper")}
                                        title={!isPresent ? "Mark Present first" : `Click to mark Supper${record.enteredBy?.supper ? ` (entered by ${record.enteredBy.supper})` : ''}`}
                                      >
                                        {record.supper && (
                                          <span className="text-purple-600 font-semibold text-base">
                                            {record.enteredBy?.supper || 'X'}
                                          </span>
                                        )}
                                      </td>
                                      {/* Attendance */}
                                      <td className="border border-green-200 p-0">
                                        <select
                                          value={record.attendance || ""}
                                          onChange={(e) => updateAttendance(child.id, day, e.target.value)}
                                          className="w-full h-full p-1 text-center bg-transparent border-0 cursor-pointer text-xs font-semibold"
                                          title={record.enteredBy?.attendance ? `Recorded by ${record.enteredBy.attendance}` : ''}
                                        >
                                          {ATTENDANCE_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                          ))}
                                        </select>
                                      </td>
                                    </React.Fragment>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </>
                )}
                {/* Daily Totals Row */}
                {children.length > 0 && (
                  <tr className="bg-green-100 font-semibold">
                    <td className="border border-green-300 p-2 sticky left-0 bg-green-100 z-10"></td>
                    <td className="border border-green-300 p-2 sticky left-8 bg-green-100 z-10 text-green-800">
                      DAILY TOTALS
                    </td>
                    {weekDays.map(day => {
                      const totals = getDailyTotals(day);
                      return (
                        <React.Fragment key={`totals-${day.toISOString()}`}>
                          <td className="border border-green-300 p-1 text-center text-amber-700">{totals.breakfast || ""}</td>
                          <td className="border border-green-300 p-1 text-center text-orange-700">{totals.amSnack || ""}</td>
                          <td className="border border-green-300 p-1 text-center text-green-700">{totals.lunch || ""}</td>
                          <td className="border border-green-300 p-1 text-center text-blue-700">{totals.pmSnack || ""}</td>
                          <td className="border border-green-300 p-1 text-center text-purple-700">{totals.supper || ""}</td>
                          <td className="border border-green-300 p-1 text-center text-xs">
                            <span className="text-emerald-700">P:{totals.present}</span>
                            <br/>
                            <span className="text-red-600">A:{totals.absent}</span>
                          </td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
        
        {/* Weekly Totals Summary */}
        {children.length > 0 && (
          <Card className="mt-4 bg-green-50 border-green-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-green-800">Weekly Totals</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                <div className="bg-amber-100 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-amber-700">{weeklyTotals.breakfast}</p>
                  <p className="text-xs text-amber-600">Breakfast</p>
                </div>
                <div className="bg-orange-100 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-orange-700">{weeklyTotals.amSnack}</p>
                  <p className="text-xs text-orange-600">AM Snack</p>
                </div>
                <div className="bg-green-100 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-700">{weeklyTotals.lunch}</p>
                  <p className="text-xs text-green-600">Lunch</p>
                </div>
                <div className="bg-blue-100 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-700">{weeklyTotals.pmSnack}</p>
                  <p className="text-xs text-blue-600">PM Snack</p>
                </div>
                <div className="bg-purple-100 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-purple-700">{weeklyTotals.supper}</p>
                  <p className="text-xs text-purple-600">Supper</p>
                </div>
                <div className="bg-emerald-100 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-emerald-700">{weeklyTotals.present}</p>
                  <p className="text-xs text-emerald-600">Present</p>
                </div>
                <div className="bg-red-100 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-red-700">{weeklyTotals.absent}</p>
                  <p className="text-xs text-red-600">Absent</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      </FeatureGate>
    </div>
    </AppShell>
  );
};

export default CACFPPage;
